import { useContext } from "react";

import { UploadContext } from "../UploadContext";
import type { UploadProgress } from "../UploadContext";

/**
 * @returns hook to track the in-progress uploads across all chats.
 */
export function useUploadProgress() {
  const context = useContext(UploadContext);
  if (context === null) {
    throw new Error("useUploadProgress must be used within a UploadProvider");
  }
  const [uploads, setUploads] = context;

  /**
   * Update the upload progress that the user sees.
   */
  const setUploadProgress = (upload: UploadProgress) => {
    const { chatId, fileName } = upload;

    setUploads((uploadMap) => {
      const chatUploads = uploadMap[chatId] ?? {};
      return {
        ...uploadMap,
        [chatId]: { ...chatUploads, [fileName]: upload },
      };
    });
  };

  /**
   * Remove a file from the set of in-progress uploads.
   */
  const removeUploadProgress = (upload: {
    chatId: string;
    fileName: string;
  }) => {
    const { chatId, fileName } = upload;
    const removeKey = (key: string, { [key]: _, ...rest }) => rest;

    setUploads((uploadMap) => {
      const chatUploads = uploadMap[chatId] ?? {};
      return {
        ...uploadMap,
        [chatId]: removeKey(fileName, chatUploads),
      };
    });
  };

  return {
    uploads,
    setUploadProgress,
    removeUploadProgress,
  };
}
